import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ShareModal from '../../components/sharing/ShareModal';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  ShareIcon,
  BookmarkIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon,
  TagIcon,
  FolderIcon,
  HeartIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

import { diaryService } from '../../services/diary';
import { format } from 'date-fns';

const EntryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const entryData = await diaryService.getEntryById(id);
        setEntry(entryData);
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load entry');
        navigate('/entries');
      }
    };

    fetchEntry();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this entry? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await diaryService.deleteEntry(id);
      toast.success('Entry deleted successfully');
      navigate('/entries');
    } catch (error) {
      toast.error('Failed to delete entry');
      setIsDeleting(false);
    }
  };

  const handlePin = async () => {
    try {
      const updatedEntry = await diaryService.updateEntry(id, {
        ...entry,
        isPinned: !entry.isPinned
      });
      setEntry(updatedEntry);
      toast.success(updatedEntry.isPinned ? 'Entry pinned' : 'Entry unpinned');
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const getMoodEmoji = (mood) => {
    const moodMap = {
      happy: '😊',
      sad: '😢',
      excited: '😍',
      angry: '😡',
      peaceful: '😌',
      anxious: '😰',
      thoughtful: '🤔',
      tired: '😴'
    };
    return moodMap[mood?.toLowerCase()] || mood || '📝';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Entry not found</h2>
          <p className="text-gray-600 mb-4">The entry you're looking for doesn't exist or has been deleted.</p>
          <Link to="/entries" className="btn-primary">Back to Entries</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={() => navigate('/entries')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </motion.button>
              <div>
                <h1 className="text-xl font-serif font-semibold text-ink-black">Entry Details</h1>
                <p className="text-sm text-gray-500">{format(new Date(entry.createdAt), 'MMMM d, yyyy')}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <motion.button
                onClick={handlePin}
                className={`p-2 rounded-lg transition-colors ${
                  entry.isPinned ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100 text-gray-600'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={entry.isPinned ? 'Unpin entry' : 'Pin entry'}
              >
                <BookmarkIcon className={`h-5 w-5 ${entry.isPinned ? 'fill-current' : ''}`} />
              </motion.button>

              <motion.button
                onClick={() => setShowShareModal(true)}
                className="p-2 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Share entry"
              >
                <ShareIcon className="h-5 w-5" />
              </motion.button>

              <motion.button
                onClick={() => navigate(`/entries/${id}/edit`)}
                className="p-2 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Edit entry"
              >
                <PencilIcon className="h-5 w-5" />
              </motion.button>

              <motion.button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Delete entry"
              >
                {isDeleting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                ) : (
                  <TrashIcon className="h-5 w-5" />
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Entry Header */}
          <motion.div className="text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center justify-center space-x-4 mb-4">
              {entry.mood && <span className="text-4xl">{getMoodEmoji(entry.mood)}</span>}
              {entry.isPinned && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.3, delay: 0.2 }}>
                  <HeartIcon className="h-6 w-6 text-red-500 fill-current" />
                </motion.div>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-ink-black mb-4">{entry.title}</h1>

            {/* Entry Metadata */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4" />
                <span>{format(new Date(entry.createdAt), 'MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <ClockIcon className="h-4 w-4" />
                <span>{format(new Date(entry.createdAt), 'h:mm a')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <EyeIcon className="h-4 w-4" />
                <span>{entry.viewCount} views</span>
              </div>
              {entry.folder && (
                <div className="flex items-center space-x-2">
                  <FolderIcon className="h-4 w-4" />
                  <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs">{entry.folder}</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Entry Content */}
          <motion.div className="diary-paper p-8 rounded-xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div
              className="prose prose-lg max-w-none text-gray-700"
              style={{ fontFamily: 'Crimson Text, serif', lineHeight: '1.8', fontSize: '18px' }}
              dangerouslySetInnerHTML={{ __html: entry.content }}
            />
          </motion.div>

          {/* Tags */}
          {entry.tags && entry.tags.length > 0 && (
            <motion.div className="flex flex-wrap items-center gap-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
              <TagIcon className="h-5 w-5 text-gray-400" />
              {entry.tags.map((tag, index) => (
                <motion.span
                  key={tag}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => navigate(`/search?tag=${tag}`)}
                >
                  #{tag}
                </motion.span>
              ))}
            </motion.div>
          )}

          {/* Entry Stats */}
          <motion.div className="card p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
            <h3 className="text-lg font-semibold text-ink-black mb-4">Entry Statistics</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">{format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Last updated:</span>
                  <span className="font-medium">{format(new Date(entry.updatedAt), 'MMM d, yyyy h:mm a')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Word count:</span>
                  <span className="font-medium">{entry.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length} words</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Views:</span>
                  <span className="font-medium">{entry.viewCount}</span>
                </div>
                {entry.isShared && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Shares:</span>
                    <span className="font-medium">{entry.shareCount}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status:</span>
                  <div className="flex items-center space-x-2">
                    {entry.isPinned && <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs">Pinned</span>}
                    {entry.isShared && <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">Shared</span>}
                    {!entry.isPinned && !entry.isShared && <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">Private</span>}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.div className="flex justify-center space-x-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}>
            <Link to={`/entries/${id}/edit`} className="btn-primary flex items-center space-x-2">
              <PencilIcon className="h-4 w-4" />
              <span>Edit Entry</span>
            </Link>
            <Link to="/entries" className="btn-secondary flex items-center space-x-2">
              <ArrowLeftIcon className="h-4 w-4" />
              <span>Back to Entries</span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* ✅ Share Modal Integration */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        entryId={entry._id}
        entryTitle={entry.title}
        onShareCreated={(shareData) => {
          window.location.reload();
        }}
      />
    </div>
  );
};

export default EntryDetail;
